import { memo, useMemo, useState } from 'react';
import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { platformFor } from '@deepdive-icons';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { spanClass, type WidgetBlockProps } from '../../widgets/shared/cardShell';
import {
  SENTIMENT_BADGE,
  pageWindow,
  tableEmptyLabel,
  wholeFormat as whole,
} from '../shared/tableShared';
import { TableWidgetActions, tableDraggingClass } from '../TableWidgetActions/TableWidgetActions';

/**
 * Competitors — `Competitive page performance` (2024:32704, in 2030:19055).
 *
 * Every brand page — one brand on one channel — with its engagement broken
 * down by type. A page is every mention of a brand on a channel; Reactions
 * carry likes, so Reactions + Comments + Shares = Total engagement.
 *
 * **Client-side sort and page**, as in the prototype: the contract returns the
 * full page list (≤ 35 rows) in fixture order, and this block sorts it.
 *
 * **Recut on 2026-09-23** (frame 931:36991). The file replaced the three volume
 * aggregates with `Sentiment`, `Intent` and `Topic`, renamed `Brand` to
 * `Company` and widened it to 370. Two departures from the file, both settled
 * with the user:
 *
 *  - **`Total posts` stays.** Only `Avg. engagement` was meant to go. A table
 *    whose rows carry no count at all cannot say what its interaction figures
 *    are built on, and the header's own "35 total pages" would be the only
 *    volume on screen.
 *  - **The ninth column is `Total engagement`, not `Engagement share`.** The
 *    file heads a column of counts `Engagement share` and hides a second
 *    column of percentages under the same name — the label travelled and the
 *    data did not.
 *
 * Three decisions the frame does not make:
 *
 *  1. **Columns sort** — every header but Channel and Page URL draws the sort
 *     chevron. Opens on Total engagement, highest first, because that is what
 *     "performance" ranks by.
 *  2. **Channel is a mark, not a button.** Figma wraps each logo in a Ghost Grey
 *     icon-only `Button`, but there is nothing for it to do.
 *  3. **No loading variant.** `Table` has none; see `tableEmptyLabel`.
 */

/** Figma draws ten body rows, which is also what its pagination implies. */
const PAGE_SIZE = 10;

/** One page, labelled. */
export type PagePerformanceRowView = {
  /** `brand:channel`, stable across renders and filters. */
  id: string;
  brand: string;
  brandLabel: string;
  channel: string;
  channelLabel: string;
  /** The page's dominant values — the plurality of its mentions, not a property of the page. */
  sentiment: string;
  sentimentLabel: string;
  intentLabel: string;
  topicLabel: string;
  posts: number;
  engagement: number;
  /** Reactions **including likes**. */
  reactions: number;
  comments: number;
  shares: number;
};

export type CompetitivePagePerformanceView = {
  /** Every page, in fixture order (brand, then channel) — the tie order of every sort. */
  pages: PagePerformanceRowView[];
};

export type PagePerformanceSortKey =
  | 'brand'
  | 'sentiment'
  | 'intent'
  | 'topic'
  | 'posts'
  | 'engagement'
  | 'reactions'
  | 'comments'
  | 'shares';
export type PagePerformanceSort = { key: PagePerformanceSortKey; direction: 'asc' | 'desc' };

export const DEFAULT_PAGE_PERFORMANCE_SORT: PagePerformanceSort = {
  key: 'engagement',
  direction: 'desc',
};

/** The three label columns sort A-Z; every figure opens highest first. */
const LABEL_KEYS = ['sentiment', 'intent', 'topic'] as const;
const opensAscending = (key: PagePerformanceSortKey) =>
  key === 'brand' || (LABEL_KEYS as readonly string[]).includes(key);

function compare(
  a: PagePerformanceRowView,
  b: PagePerformanceRowView,
  key: PagePerformanceSortKey,
): number {
  if (key === 'brand') {
    /* By name, then channel, so a brand's pages stay together. */
    return a.brandLabel.localeCompare(b.brandLabel) || a.channelLabel.localeCompare(b.channelLabel);
  }
  /*
   * The dominant-value columns sort by their **label**, not by fixture order:
   * the reader is looking at words. Ties fall back to the page.
   */
  if (key === 'sentiment')
    return a.sentimentLabel.localeCompare(b.sentimentLabel) || compare(a, b, 'brand');
  if (key === 'intent') return a.intentLabel.localeCompare(b.intentLabel) || compare(a, b, 'brand');
  if (key === 'topic') return a.topicLabel.localeCompare(b.topicLabel) || compare(a, b, 'brand');

  return a[key] - b[key];
}

/** The table's order for a sort — exported for the parity test. */
export function sortPagePerformance(
  pages: readonly PagePerformanceRowView[],
  sort: PagePerformanceSort,
): PagePerformanceRowView[] {
  const out = [...pages].sort((a, b) => compare(a, b, sort.key));
  return sort.direction === 'desc' ? out.reverse() : out;
}

export type CompetitivePagePerformanceTableProps = WidgetBlockProps & {
  view?: CompetitivePagePerformanceView;
  /**
   * Figma `Page URL` → `View`. A no-op until there is somewhere to send it;
   * the hook is here so that becomes a change at the call site.
   */
  onViewPage?: (page: PagePerformanceRowView) => void;
  /**
   * Open the row — a click anywhere on it, or the Company text, which turns
   * into the row's link on hover (Figma 2024:32704). Competitors opens the Page
   * conversations modal (2125:84169). Omit and the rows stay inert, which is
   * what a custom report widget of this table wants.
   */
  onOpenPage?: (page: PagePerformanceRowView) => void;
};

export const CompetitivePagePerformanceTable = memo(function CompetitivePagePerformanceTable({
  status,
  view,
  span = 12,
  chrome,
  onViewPage,
  onOpenPage,
}: CompetitivePagePerformanceTableProps) {
  const [sort, setSort] = useState<PagePerformanceSort>(DEFAULT_PAGE_PERFORMANCE_SORT);
  const [page, setPage] = useState(1);

  const pages = useMemo(() => (status === 'ready' && view ? view.pages : []), [status, view]);
  const sorted = useMemo(() => sortPagePerformance(pages, sort), [pages, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  /* Clamped on read rather than reset in an effect — Influencers' reason. */
  const currentPage = Math.min(page, pageCount);
  const visible = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const sortFor = (key: PagePerformanceSortKey): TableHeaderSort =>
    sort.key === key ? sort.direction : 'selector';

  /*
   * A re-sort returns to page 1. Staying on page 4 of a new order shows ten
   * rows the reader has no way to place.
   */
  const onSort = (key: PagePerformanceSortKey) => () => {
    setPage(1);
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : /* A new column opens descending for every figure; words open A–Z. */
          { key, direction: opensAscending(key) ? 'asc' : 'desc' },
    );
  };

  const sortable = (
    key: PagePerformanceSortKey,
    header: string,
    width: string,
    extra?: Partial<TableColumn>,
  ): TableColumn => ({
    key,
    header,
    width,
    sort: sortFor(key),
    onSort: onSort(key),
    ...extra,
  });

  /*
   * Widths as a share of the frame's 1192. Seven of the eleven are the file's
   * own after the 2026-09-23 recut — 70 / 99 / 97 / 103 / 81 / 146 / 78 — and
   * the other four are **rebalanced out of `Company`**, which the recut left
   * holding 370:
   *
   *   Company 370 → 176   Intent 75 → 123   Topic 73 → 129   + Total posts 90
   *
   * The file fills `Intent` and `Topic` with `12,975` and sized them for it;
   * the real values are words (`Recommendation`, `Network coverage`), sized to
   * the widest value in each dimension so a filter change cannot clip them.
   * Still 1192. Settled with the user 2026-09-23 after measuring.
   *
   * No `helpText` on any sortable column (button-in-a-button); the
   * explanation lives in the table header's tooltip instead.
   */
  const columns: TableColumn[] = [
    sortable('brand', 'Company', '14.8%'),
    /* The marks carry no text, so the column head is the only thing naming them. */
    { key: 'channel', header: 'Channel', cellType: 'actionIcons', width: '5.9%' },
    sortable('sentiment', 'Sentiment', '8.3%', { cellType: 'badge' }),
    sortable('intent', 'Intent', '10.3%'),
    sortable('topic', 'Topic', '10.8%'),
    sortable('posts', 'Total posts', '7.6%'),
    sortable('reactions', 'Reactions', '8.1%'),
    sortable('comments', 'Comments', '8.6%'),
    sortable('shares', 'Shares', '6.8%'),
    sortable('engagement', 'Total engagement', '12.2%'),
    { key: 'url', header: 'Page URL', cellType: 'actionButtons', width: '6.5%' },
  ];

  const tableRows: TableRowData[] = visible.map((row) => {
    /* The key on mock data, the name on live (where the key is an id). */
    const platform = platformFor(row.channel) ?? platformFor(row.channelLabel);
    return {
      key: row.id,
      onActivate: onOpenPage ? () => onOpenPage(row) : undefined,
      activateLabel: onOpenPage
        ? `Open conversations for ${row.brandLabel} on ${row.channelLabel}`
        : undefined,
      cells: {
        brand: { text: row.brandLabel },
        channel: {
          content: platform ? (
            <PlatformIcon name={platform.name} label={row.channelLabel} />
          ) : (
            /* A channel with no drawn mark falls back to its name, as ChartLegend does. */
            row.channelLabel
          ),
        },
        sentiment: {
          content: (
            <Badge type={SENTIMENT_BADGE[row.sentiment] ?? 'gray'}>{row.sentimentLabel}</Badge>
          ),
        },
        intent: { text: row.intentLabel },
        topic: { text: row.topicLabel },
        posts: { text: whole.format(row.posts) },
        reactions: { text: whole.format(row.reactions) },
        comments: { text: whole.format(row.comments) },
        shares: { text: whole.format(row.shares) },
        engagement: { text: whole.format(row.engagement) },
        url: {
          content: (
            <Button
              hierarchy="ghostColor"
              iconTrailing={<Icon name="arrowUpRight" />}
              onClick={() => onViewPage?.(row)}
              aria-label={`View ${row.brandLabel} on ${row.channelLabel}`}
            >
              View
            </Button>
          ),
        },
      },
    };
  });

  const count = pages.length;

  return (
    <Table
      className={[spanClass(span), tableDraggingClass(chrome)].filter(Boolean).join(' ')}
      label="Competitive page performance"
      columns={columns}
      rows={tableRows}
      emptyLabel={tableEmptyLabel(status, 'No pages in this period')}
      header={
        <TableHeader
          flash={chrome?.flash}
          title="Competitive page performance"
          badge={`${whole.format(count)} total ${count === 1 ? 'page' : 'pages'}`}
          helpText="Every brand's presence on every channel, what its conversation is mostly about, and the engagement it drew."
          helpSupportingText="A page is one brand on one channel. Sentiment, Intent and Topic are the most common value across that page's mentions — a plurality, not the whole picture. Reactions include likes, so Reactions, Comments and Shares add up to Total engagement on every row."
          actions={<TableWidgetActions chrome={chrome} title="Competitive page performance" />}
        />
      }
      pagination={
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          /* Five numbers, as the frame draws — see `pageWindow`. */
          pages={pageWindow(currentPage, pageCount)}
          onPageChange={setPage}
          label="Page performance pages"
        />
      }
    />
  );
});
